import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';

interface PermissionGuardProps {
  module: string;
  children: React.ReactNode;
  /**
   * Deny-by-default mode for opt-in modules (e.g. billing). When set:
   *  - platform admins do NOT bypass the check, and
   *  - an empty permission set does NOT fall back to "allow all".
   * Access is granted only with an explicit `<module>.view` grant. Do NOT use
   * `strict` on lockout-critical modules like `roles` — admins must always be
   * able to reach the Roles/Permissions page to grant permissions.
   */
  strict?: boolean;
}

export function PermissionGuard({ module, children, strict = false }: PermissionGuardProps) {
  const { user, isLoading } = useAuth();
  const { canView, canViewStrict } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Strict modules are deny-by-default: no admin bypass, no empty-permissions
  // fallback. Visible only with an explicit `<module>.view` grant.
  if (strict) {
    return canViewStrict(module) ? <>{children}</> : <Navigate to="/dashboard" replace />;
  }

  // If user has no permissions yet (still loading from /me), allow access
  // to avoid redirect loops. Admin users always have access.
  if (!user?.permissions?.length || user.isAdmin) {
    return <>{children}</>;
  }

  if (!canView(module)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
